import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { DialogService } from '../../../../shared/services/dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { LabCustomTagService } from '../../services/lab-custom-tag.service';
import { CustomTagDataType, ILabCustomTag, ILabCustomTagCreate } from '../../models/LabCustomTag';

export const CREATE_CUSTOM_TAG_MODAL_ID = 'create_custom_tag_modal';

/**
 * Modal for deliberately creating a new lab custom post-analysis tag. Mirrors
 * {@link SuggestOptionModalComponent}'s dialog wiring. Collects a name, data
 * type, an optional private (lab-only) flag, and — for SELECT tags — an option
 * list (single or multi-select). On success it emits the created tag so the
 * host can select it into the draft editor.
 */
@Component({
  selector: 'app-create-custom-tag-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './create-custom-tag-modal.component.html',
})
export class CreateCustomTagModalComponent implements AfterViewInit, OnDestroy {
  /** File whose lab the new custom tag is scoped to. */
  @Input({ required: true }) fileId!: number;
  @Output() created = new EventEmitter<ILabCustomTag>();

  @ViewChild('modalEl') modalEl?: ElementRef<HTMLDialogElement>;

  readonly modalId = CREATE_CUSTOM_TAG_MODAL_ID;
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(LabCustomTagService);
  private readonly dialog = inject(DialogService);
  private readonly toast = inject(ToastService);

  readonly dataTypes: { value: CustomTagDataType; label: string }[] = [
    { value: 'TEXT', label: 'Text' },
    { value: 'NUMBER', label: 'Number' },
    { value: 'DATE', label: 'Date' },
    { value: 'SELECT', label: 'Select (fixed options)' },
    { value: 'GPS', label: 'GPS coordinates' },
    { value: 'LIST', label: 'List (free-form values)' },
  ];

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    data_type: ['TEXT' as CustomTagDataType, [Validators.required]],
    is_private: [false],
    multiple_select: [false],
  });

  /** SELECT option values being authored (chips). */
  options: string[] = [];
  submitting = false;
  errorMessage = '';

  ngAfterViewInit(): void {
    if (this.modalEl) this.dialog.register(this.modalId, this.modalEl.nativeElement);
  }
  ngOnDestroy(): void {
    this.dialog.unregister(this.modalId);
  }

  get isSelect(): boolean {
    return this.form.value.data_type === 'SELECT';
  }

  open(): void {
    this.reset();
    this.dialog.open(this.modalId);
  }

  close(): void {
    this.dialog.close(this.modalId);
  }

  reset(): void {
    this.form.reset({ name: '', data_type: 'TEXT', is_private: false, multiple_select: false });
    this.options = [];
    this.submitting = false;
    this.errorMessage = '';
  }

  addOption(input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;
    if (!this.options.some((o) => o.toUpperCase() === value.toUpperCase())) {
      this.options = [...this.options, value];
    }
    input.value = '';
  }

  removeOption(index: number): void {
    this.options = this.options.filter((_, i) => i !== index);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isSelect && this.options.length === 0) {
      this.errorMessage = 'Add at least one option for a Select tag.';
      return;
    }

    const dataType = this.form.value.data_type as CustomTagDataType;
    const payload: ILabCustomTagCreate = {
      file: this.fileId,
      name: this.form.value.name,
      data_type: dataType,
      is_private: !!this.form.value.is_private,
    };
    if (dataType === 'SELECT') {
      payload.multiple_select = !!this.form.value.multiple_select;
      payload.options = this.options;
    }

    this.submitting = true;
    this.errorMessage = '';
    this.service.create(payload).subscribe({
      next: (tag) => {
        this.submitting = false;
        this.toast.show(`Custom tag "${tag.name}" created.`, 'success');
        this.created.emit(tag);
        this.close();
      },
      error: (err) => {
        this.submitting = false;
        const data = err?.error;
        const firstError = data
          ? typeof data === 'string'
            ? data
            : Object.values(data).flat().join(' ')
          : 'Failed to create custom tag';
        this.errorMessage = String(firstError);
      },
    });
  }
}
